import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneIncoming,
  PhoneForwarded,
  Mic,
  MicOff,
  Delete,
  History,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Wifi,
  WifiOff,
  Loader2,
  ShieldAlert,
  User,
  Check,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/ToastContext';
import { useSipPhoneContext } from '@/contexts/SipPhoneContext';
import {
  listMyCallLog,
  listTransferTargets,
  lookupCaller,
  updateCallNotes,
  type CallLogRow,
  type CallStatus,
  type TransferTarget,
} from '@/lib/sipApi';
import { supabase } from '@/lib/supabaseClient';

const DIAL_KEYS = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
] as const;

// Standard dual-tone frequency pairs — played locally for tactile/audible
// feedback on every keypad press (Microsip does the same), independent of
// the real DTMF signal sent to the far end over the SIP session.
const DTMF_FREQ: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

function playLocalTone(digit: string, ctxRef: { current: AudioContext | null }) {
  const freqs = DTMF_FREQ[digit];
  if (!freqs) return;
  try {
    if (!ctxRef.current) ctxRef.current = new AudioContext();
    const ctx = ctxRef.current;
    const gain = ctx.createGain();
    gain.gain.value = 0.05;
    gain.connect(ctx.destination);
    freqs.forEach((f) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      osc.connect(gain);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    });
  } catch {
    // Web Audio can be blocked/unavailable in some contexts — the beep is
    // a nicety, not load-bearing, so fail silently.
  }
}

// Exported for AppShell's sitewide in-call banner, which needs the exact
// same mm:ss format.
export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const STATUS_LABEL: Record<CallStatus, string> = {
  ringing: 'Ringing',
  completed: 'Completed',
  no_answer: 'No Answer',
  busy: 'Busy',
  failed: 'Failed',
  missed: 'Missed',
  rejected: 'Rejected',
  canceled: 'Canceled',
  transferred: 'Transferred',
};

const STATUS_TONE: Record<CallStatus, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  ringing: 'primary',
  completed: 'success',
  no_answer: 'warning',
  busy: 'warning',
  failed: 'error',
  missed: 'error',
  rejected: 'error',
  canceled: 'neutral',
  transferred: 'primary',
};

// No AppShell here on purpose — this renders as one tab inside the
// Communications hub (see admin/communications/page.tsx), which owns the
// single AppShell for both this and SmsContent.
export function PhoneContent() {
  const { push } = useToast();
  const phone = useSipPhoneContext();
  const [number, setNumber] = useState('');
  const audioCtxRef = useRef<AudioContext | null>(null);

  const [history, setHistory] = useState<CallLogRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Debounced "who is this" lookup while dialing — same customer/order match
  // used for incoming calls (useSipPhone's callerInfo), just run locally
  // here since nothing is being dialed yet.
  const [dialLookupName, setDialLookupName] = useState<string | null>(null);
  useEffect(() => {
    if (number.trim().length < 7) {
      setDialLookupName(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      lookupCaller(number.trim())
        .then((info) => !cancelled && setDialLookupName(info?.customer_name ?? null))
        .catch(() => {});
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [number]);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargets, setTransferTargets] = useState<TransferTarget[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferBusyId, setTransferBusyId] = useState<string | null>(null);

  const openTransfer = async () => {
    setTransferOpen(true);
    setTransferLoading(true);
    try {
      setTransferTargets(await listTransferTargets());
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setTransferLoading(false);
    }
  };

  const doTransfer = async (t: TransferTarget) => {
    setTransferBusyId(t.user_id);
    try {
      await phone.transfer(t.extension, t.display_name);
      push('success', `Transferred to ${t.display_name} (Ext. ${t.extension})`);
      setTransferOpen(false);
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setTransferBusyId(null);
    }
  };

  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  useEffect(() => {
    setNoteDraft('');
    setNoteSaved(false);
  }, [phone.lastCallLogId]);

  const saveNote = async () => {
    if (!phone.lastCallLogId || !noteDraft.trim()) return;
    setNoteSaving(true);
    try {
      await updateCallNotes(phone.lastCallLogId, noteDraft.trim());
      setNoteSaved(true);
      loadHistory();
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setNoteSaving(false);
    }
  };

  const loadHistory = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    setHistoryLoading(true);
    try {
      setHistory(await listMyCallLog(user.id));
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setHistoryLoading(false);
    }
  }, [push]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Refresh the history list whenever a call just wrapped up, so the new
  // row (and its final status/duration) shows up without a manual refresh.
  const prevCallStateRef = useRef(phone.callState);
  useEffect(() => {
    if (prevCallStateRef.current !== 'ended' && phone.callState === 'ended') {
      setTimeout(loadHistory, 600);
    }
    prevCallStateRef.current = phone.callState;
  }, [phone.callState, loadHistory]);

  const pressKey = (digit: string) => {
    if (phone.callState === 'active') {
      phone.sendDTMF(digit);
      playLocalTone(digit, audioCtxRef);
    } else {
      setNumber((n) => n + digit);
      playLocalTone(digit, audioCtxRef);
    }
  };

  const handleCall = () => {
    if (!number.trim()) {
      push('error', 'Enter a number to call');
      return;
    }
    if (phone.registrationStatus !== 'registered') {
      push('error', 'Your softphone is not registered yet — wait for the green status, or check your SIP settings.');
      return;
    }
    phone.call(number.trim());
  };

  const inCall = phone.callState === 'dialing' || phone.callState === 'ringing' || phone.callState === 'active';

  const regBadge = useMemo(() => {
    switch (phone.registrationStatus) {
      case 'registered':
        return { tone: 'success' as const, icon: <Wifi className="w-3 h-3 mr-1 inline" />, label: `Registered — Ext. ${phone.extension}` };
      case 'connecting':
        return { tone: 'warning' as const, icon: <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />, label: 'Connecting…' };
      case 'unregistered':
        return { tone: 'neutral' as const, icon: <WifiOff className="w-3 h-3 mr-1 inline" />, label: 'Unregistered' };
      case 'failed':
        return { tone: 'error' as const, icon: <WifiOff className="w-3 h-3 mr-1 inline" />, label: phone.registrationError || 'Registration failed' };
      case 'no_credentials':
        return { tone: 'error' as const, icon: <ShieldAlert className="w-3 h-3 mr-1 inline" />, label: 'No SIP extension assigned' };
      default:
        return { tone: 'neutral' as const, icon: <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />, label: 'Loading…' };
    }
  }, [phone.registrationStatus, phone.registrationError, phone.extension]);

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return history;
    return history.filter((r) => (r.counterparty_number ?? '').toLowerCase().includes(q));
  }, [history, search]);

  return (
    <>
      <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Phone</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">Make and receive calls right from the browser — powered by your own SIP extension.</p>
        </div>
        <Badge tone={regBadge.tone}>
          {regBadge.icon}
          {regBadge.label}
        </Badge>
      </div>

      {phone.registrationStatus === 'no_credentials' && (
        <Card className="border-error-border bg-error-bg">
          <CardBody className="flex items-center gap-3 py-3">
            <ShieldAlert className="w-4.5 h-4.5 text-error flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-error">No SIP extension assigned to your account yet</p>
              <p className="text-[11.5px] text-ink-faint">Ask an admin to assign you one from ERP: Settings &gt; SIP Accounts.</p>
            </div>
          </CardBody>
        </Card>
      )}

      {phone.micPermission === 'denied' && (
        <Card className="border-error-border bg-error-bg">
          <CardBody className="flex items-center gap-3">
            <MicOff className="w-5 h-5 text-error flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-error">Microphone access is blocked</p>
              <p className="text-[11.5px] text-ink-faint">Calls need your microphone — allow access in your browser's site settings, then reload this page.</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* The incoming-call popup itself now lives in AppShell (renders on every
          admin page, not just this one) — see components/layout/AppShell.tsx. */}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-5">
          <Card>
            <CardHeader title="Dialer" icon={<Phone className="w-4 h-4" />} />
            <CardBody className="space-y-3">
              {inCall ? (
                <div className="flex flex-col items-center gap-1.5 py-2">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      phone.callState === 'active' ? 'bg-success-bg text-success' : 'bg-primary-50 text-primary-800 animate-pulse'
                    }`}
                  >
                    <PhoneCall className="w-7 h-7" />
                  </div>
                  {phone.callerInfo?.customer_name && (
                    <p className="text-[13px] font-semibold text-primary-800 flex items-center gap-1">
                      <User className="w-3 h-3" /> {phone.callerInfo.customer_name}
                      {phone.callerInfo.orders_count > 0 && (
                        <span className="text-ink-faint font-normal">
                          · {phone.callerInfo.orders_count} order{phone.callerInfo.orders_count === 1 ? '' : 's'}
                        </span>
                      )}
                    </p>
                  )}
                  <p className="text-[16px] font-bold text-ink">{phone.remoteNumber || number}</p>
                  <p className="text-[12.5px] text-ink-faint">
                    {phone.callState === 'dialing' && 'Dialing…'}
                    {phone.callState === 'ringing' && phone.callDirection === 'outbound' && 'Ringing…'}
                    {phone.callState === 'active' && formatDuration(phone.callSeconds)}
                  </p>
                </div>
              ) : phone.callState === 'ended' ? (
                <div className="flex flex-col items-center gap-1.5 py-2 w-full">
                  <div className="w-16 h-16 rounded-full bg-surface-muted text-ink-faint flex items-center justify-center">
                    <PhoneOff className="w-7 h-7" />
                  </div>
                  <p className="text-[13px] font-semibold text-ink">Call ended</p>
                  {phone.lastEndedStatus && (
                    <Badge tone={STATUS_TONE[phone.lastEndedStatus]}>
                      {STATUS_LABEL[phone.lastEndedStatus]}
                    </Badge>
                  )}
                  {phone.lastCallLogId && (
                    <div className="w-full space-y-1.5 pt-1">
                      <Textarea
                        value={noteDraft}
                        onChange={(e) => {
                          setNoteDraft(e.target.value);
                          setNoteSaved(false);
                        }}
                        placeholder="Add a note about this call…"
                        rows={2}
                        className="text-[12.5px]"
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full"
                        onClick={saveNote}
                        loading={noteSaving}
                        disabled={!noteDraft.trim() || noteSaved}
                      >
                        {noteSaved ? <Check className="w-3.5 h-3.5" /> : null}
                        {noteSaved ? 'Saved' : 'Save Note'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Field label="Number">
                  <Input
                    value={number}
                    onChange={(e) => setNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
                    placeholder="e.g. 01712345678"
                    inputMode="tel"
                    className="text-center text-[18px] font-semibold tracking-wide"
                  />
                  {dialLookupName && (
                    <p className="text-[12px] text-primary-800 font-medium mt-1.5 flex items-center justify-center gap-1">
                      <User className="w-3 h-3" /> {dialLookupName}
                    </p>
                  )}
                </Field>
              )}

              <div className="grid grid-cols-3 gap-1.5 max-w-[220px] mx-auto">
                {DIAL_KEYS.map(([digit, letters]) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => pressKey(digit)}
                    className="h-11 rounded-xl bg-surface-muted hover:bg-primary-50 active:scale-95 transition-all flex flex-col items-center justify-center cursor-pointer"
                  >
                    <span className="text-[14px] font-bold text-ink leading-none">{digit}</span>
                    {letters && <span className="text-[6px] text-ink-disabled tracking-widest mt-0.5">{letters}</span>}
                  </button>
                ))}
              </div>

              {!inCall && phone.callState !== 'ended' && number.length > 0 && (
                <button
                  type="button"
                  onClick={() => setNumber((n) => n.slice(0, -1))}
                  className="w-full flex items-center justify-center gap-1.5 text-[12px] text-ink-faint hover:text-ink py-1 cursor-pointer"
                >
                  <Delete className="w-3.5 h-3.5" /> Backspace
                </button>
              )}

              <div className="flex items-center gap-2.5 flex-wrap">
                {inCall ? (
                  <>
                    <Button variant="secondary" className="flex-1" onClick={phone.toggleMute} disabled={phone.callState !== 'active'}>
                      {phone.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      {phone.muted ? 'Unmute' : 'Mute'}
                    </Button>
                    {phone.callState === 'active' && (
                      <Button variant="secondary" className="flex-1" onClick={openTransfer}>
                        <PhoneForwarded className="w-4 h-4" /> Transfer
                      </Button>
                    )}
                    <Button variant="danger" className="flex-1" onClick={phone.hangup}>
                      <PhoneOff className="w-4 h-4" /> Hang Up
                    </Button>
                  </>
                ) : (
                  <Button className="w-full" onClick={handleCall} disabled={phone.registrationStatus !== 'registered'}>
                    <PhoneCall className="w-4 h-4" /> Call
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card>
            <CardHeader
              title="Your History"
              subtitle={historyLoading ? undefined : `${history.length} call${history.length === 1 ? '' : 's'} — full history across all staff is in the ERP`}
              icon={<History className="w-4 h-4" />}
              action={
                <button
                  type="button"
                  onClick={loadHistory}
                  title="Refresh"
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              }
            />
            <CardBody className="space-y-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                <Input placeholder="Search by phone number" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
              </div>

              {historyLoading ? (
                <Spinner />
              ) : filteredHistory.length === 0 ? (
                <EmptyState
                  icon={<History className="w-5 h-5" />}
                  title={history.length === 0 ? 'No calls yet' : 'No matches'}
                  hint={history.length === 0 ? 'Calls you make or receive will show up here.' : 'Try a different phone number.'}
                />
              ) : (
                <div className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
                  {filteredHistory.map((r) => {
                    const expanded = expandedId === r.id;
                    return (
                      <div key={r.id} className="rounded-xl border border-border-subtle overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : r.id)}
                          className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-surface-muted cursor-pointer text-left"
                        >
                          <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-semibold text-ink truncate">
                              {r.direction === 'outbound' ? '↗ ' : '↙ '}
                              {r.counterparty_number || 'Unknown'}
                            </p>
                            <p className="text-[11px] text-ink-faint mt-0.5">
                              {new Date(r.started_at).toLocaleString()}
                              {r.duration_seconds != null && r.duration_seconds > 0 ? ` · ${formatDuration(r.duration_seconds)}` : ''}
                            </p>
                          </div>
                          {expanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-ink-faint flex-shrink-0 mt-0.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-ink-faint flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                        {expanded && (
                          <div className="px-3 pb-3 border-t border-border-subtle pt-2.5 space-y-1 text-[12px] text-ink-muted">
                            <p>Direction: {r.direction === 'outbound' ? 'Outgoing' : 'Incoming'}</p>
                            <p>Started: {new Date(r.started_at).toLocaleString()}</p>
                            {r.ended_at && <p>Ended: {new Date(r.ended_at).toLocaleString()}</p>}
                            {r.transferred_to && <p>Transferred to: {r.transferred_to}</p>}
                            {r.fail_reason && <p className="text-error">Reason: {r.fail_reason}</p>}
                            {r.notes && <p className="text-ink italic">"{r.notes}"</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
      </div>

      <Modal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="Transfer Call"
        subtitle="Pick who to send this call to"
        icon={<PhoneForwarded className="w-3.5 h-3.5" />}
      >
        {transferLoading ? (
          <Spinner />
        ) : transferTargets.length === 0 ? (
          <EmptyState title="No other staff have an active extension" hint="Ask an admin to set one up from ERP: Settings > SIP Accounts." />
        ) : (
          <div className="space-y-1.5">
            {transferTargets.map((t) => (
              <button
                key={t.user_id}
                type="button"
                onClick={() => doTransfer(t)}
                disabled={!!transferBusyId}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border-subtle hover:bg-surface-muted hover:border-primary-200 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-700 to-primary-900 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                  {t.display_name.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-ink truncate">{t.display_name}</p>
                  <p className="text-[11px] text-ink-faint">Ext. {t.extension}</p>
                </div>
                {transferBusyId === t.user_id && <Loader2 className="w-4 h-4 animate-spin text-primary-700 flex-shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
